/**
 * Client for the survey-number (cadastral) map API, `/api/property/cadastral`.
 * Telangana parcels come from our own database (filled on demand from TGRAC);
 * Andhra Pradesh parcels are looked up live from APSAC (Bhuvan as fallback).
 * Public — no sign-in or geo pass needed.
 */
import axios from "axios";
import API_BASE_URI from "../../config/api.config";

// Same override as geoApi: point the map calls at a local property service.
const BASE = `${import.meta.env.VITE_GEO_BASE_URI || API_BASE_URI}/api/property/cadastral`;
const http = axios.create({ baseURL: BASE, timeout: 30000 });

const item = (res) => res.data?.data?.item ?? null;

const CELL_TTL_MS = 15 * 60 * 1000;
const MAX_CELLS = 120; // ~ a few screens of map; dense cells are ~1–3 MB parsed
const cellMemo = new Map(); // "x/y" → { promise, until }, oldest first

// Slippy-map cell numbers at zoom 15 — the grid the parcel cache is keyed on.
export const CELL_Z = 15;
export const lng2cellX = (lng) => Math.floor(((lng + 180) / 360) * 2 ** CELL_Z);
export const lat2cellY = (lat) =>
  Math.floor(
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
      2 ** CELL_Z
  );
const items = (res) => res.data?.data?.items ?? [];

export const cadastralApi = {
  /** "telangana" | "andhra-pradesh" | null */
  region: async (lat, lng, { signal } = {}) =>
    (item(await http.get("/region", { params: { lat, lng }, signal })) || {}).state || null,
  /**
   * Telangana parcels of one zoom-15 cell (~1.2 km square) as GeoJSON. Each
   * cell is kept in memory for 15 minutes (and the browser/Redis cache it for
   * 15 minutes too), so panning back over an area asks nothing again.
   */
  cell: (x, y) => {
    const k = `${x}/${y}`;
    const hit = cellMemo.get(k);
    if (hit && hit.until > Date.now()) {
      // Most-recently-used order, for the size cap below.
      cellMemo.delete(k);
      cellMemo.set(k, hit);
      return hit.promise;
    }
    const promise = http
      .get(`/cell/${x}/${y}`, { timeout: 60000 })
      .then(item)
      .then((fc) => {
        // A cell still loading at the source isn't remembered.
        if (fc && fc.complete === false) cellMemo.delete(k);
        return fc;
      })
      .catch((err) => {
        cellMemo.delete(k); // don't remember failures
        throw err;
      });
    cellMemo.set(k, { promise, until: Date.now() + CELL_TTL_MS });
    // Cap memory: drop the least recently used cells beyond MAX_CELLS.
    while (cellMemo.size > MAX_CELLS) cellMemo.delete(cellMemo.keys().next().value);
    return promise;
  },
  /** { state, parcel (GeoJSON feature | null), source, disclaimer } */
  identify: async (lat, lng, { signal } = {}) =>
    item(await http.get("/identify", { params: { lat, lng }, signal })),
  /**
   * Survey parcels around a plot, bbox [w, s, e, n] (at most ~1.3 km a side)
   * → { state, features, source, disclaimer }.
   */
  around: async (bbox, { signal } = {}) =>
    item(await http.get("/around", { params: { bbox: bbox.map((v) => v.toFixed(6)).join(",") }, signal })),
  /**
   * Government market value (IGRS basic value) for a survey number:
   * dmv = the parcel's 7-digit village code. Andhra Pradesh by default →
   * { village, sro, effectiveDate, source, survey|null, classes, residential|null };
   * state "telangana" → { state, village, source, agri|null, nonAgri|null }.
   * null when the register has nothing for the village.
   */
  marketValue: async (dmv, sy, { signal, state, village } = {}) =>
    item(await http.get("/market-value", { params: { dmv, sy, state, village }, signal })),
  /** Telangana villages matching a name: [{ dmv, village, mandal, district, bbox }] */
  villages: async (q, { signal } = {}) => items(await http.get("/villages", { params: { q }, signal })),
  /** A survey number in a village → { features, bbox } */
  search: async (dmv, sy, { signal } = {}) =>
    item(await http.get("/search", { params: { dmv, sy }, signal })),
};

// APSAC's statewide AP cadastral tile cache (Web Mercator XYZ, labelled from
// zoom 16). Bhuvan's WMS below is the fallback.
export const AP_TILE_URL =
  "https://apsac.ap.gov.in/gisserver/rest/services/REVENUE/cadastral_ap_cache/MapServer/tile/{z}/{y}/{x}";
// Bhuvan's statewide AP cadastral layer (renders only at 1:10,000 or closer).
export const AP_WMS_URL = "https://bhuvan-vec1.nrsc.gov.in/bhuvan/gwc/service/wms";
export const AP_WMS_LAYER = "cadastral:AP_Cad";
